import { useEffect, useState } from "react";
import styles from "./my-account-page.module.css";
import { useSignOutHandler } from "hooks/use-sign-out-handler";
import { CTAButton } from "BEDROCK/shared-components/cta-button";
import { usePaths } from "BEDROCK/paths";
import { TextField as Input } from "BEDROCK/shared-components/textfield";
import { isEmailValid } from "BEDROCK/utility/validators/form-validation";
import { AlertModal } from "BEDROCK/shared-components/alert-modal";
import { useAuthContext } from "BEDROCK/auth/auth-provider";

interface MyAccountPageProps {}

export const MyAccountPage = (props: MyAccountPageProps) => {
  return (
    <div className={styles.container}>
      <div className={styles.innerContainer}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <h1>My Account</h1>
          </div>
        </div>
        <div className={styles.body}>
          <EmailSection />
          <PasswordSection />
          <AccountControlsSection />
        </div>
      </div>
    </div>
  );
};

interface EmailSectionProps {}

const EmailSection = (props: EmailSectionProps) => {
  const [isChangeingEmail, setIsChangingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState<string | undefined>();
  const [isPartiallyConfirmed, setIsPartiallyConfirmed] = useState(false);
  const { email } = useAuthContext();

  useEffect(() => {
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace(/^#/, ""));

    const message = params.get("message");
    //One email address is confirmed, tell the user to confirm the other.
    if (message) {
      setIsPartiallyConfirmed(true);
    }
  }, []);

  if (isPartiallyConfirmed) {
    return (
      <div className={styles.section}>
        <div className={styles.leadingContent}>
          <h2>Email</h2>
          <p>
            First confirmation link accepted.
            <br />
            <b>Please confirm link sent to the other email</b>
          </p>
        </div>
      </div>
    );
  }

  if (newEmail) {
    return (
      <div className={styles.section}>
        <div className={styles.leadingContent}>
          <h2>Email</h2>
          <p>
            Confirmation links have been sent to <b>both</b> your new email
            address <b>{newEmail}</b>, and your old email address <b>{email}</b>
          </p>
          <p>
            Please check your inboxes, and follow both links to confirm the
            change.
          </p>
        </div>
      </div>
    );
  }

  if (isChangeingEmail && email) {
    return (
      <ChangeEmailSection
        email={email}
        onCancel={() => {
          setIsChangingEmail(false);
        }}
        onSave={setNewEmail}
      />
    );
  }

  return (
    <div className={styles.section}>
      <div className={styles.leadingContent}>
        <h2>Email</h2>
        <p>{email ? email : "No email address found"}</p>
      </div>
      <div className={styles.trailingContent}>
        <CTAButton
          title="Change email"
          variant="LINK"
          onClick={() => {
            setIsChangingEmail(true);
          }}
        />
      </div>
    </div>
  );
};

interface ChangeEmailSectionProps {
  email: string;
  onCancel: () => void;
  onSave: (email: string) => void;
}

const ChangeEmailSection = (props: ChangeEmailSectionProps) => {
  const { changeEmailAddress } = useAuthContext();
  const [newEmail, setNewEmail] = useState(props.email);

  const [canSave, setCanSave] = useState(false);

  useEffect(() => {
    const isValid = isEmailValid(newEmail);
    const isDifferent = newEmail !== props.email;
    setCanSave(isValid && isDifferent);
  }, [newEmail, props.email]);

  return (
    <div className={styles.section}>
      <div className={styles.leadingContent}>
        <h2>Email</h2>
        <Input value={newEmail} onChangeText={setNewEmail} />
        <CTAButton
          title="Save"
          disabled={!canSave}
          variant="LINK"
          onClick={() => {
            changeEmailAddress(newEmail).then(() => {
              props.onSave(newEmail);
            });
          }}
        />
      </div>
      <div className={styles.trailingContent}>
        <CTAButton
          title="Cancel"
          variant="LINK"
          onClick={() => {
            props.onCancel();
          }}
        />
      </div>
    </div>
  );
};

interface PasswordSectionProps {}

const PasswordSection = (props: PasswordSectionProps) => {
  const { resetPassword, email } = useAuthContext();

  const [didTriggerReset, setDidTriggerReset] = useState(false);

  if (didTriggerReset) {
    return (
      <div className={styles.section}>
        <div className={styles.leadingContent}>
          <h2>Password</h2>
          <p>
            A password reset link has been sent to <b>{email}</b>.
          </p>
          <p>
            <b>Please check your inbox and follow the confirmation link.</b>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.section}>
      <div className={styles.leadingContent}>
        <h2>Password</h2>
      </div>
      <div className={styles.trailingContent}>
        <CTAButton
          title="Reset password"
          variant="LINK"
          onClick={() => {
            resetPassword().then(() => {
              setDidTriggerReset(true);
            });
          }}
        />
      </div>
    </div>
  );
};

interface AccountControlsSectionProps {}

const AccountControlsSection = (props: AccountControlsSectionProps) => {
  const { requestAccountDeletion, fetchUserProfile, userProfile } =
    useAuthContext();
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [isMarkedForRemoval, setIsMarkedForRemoval] = useState(false);
  const { signOut } = useSignOutHandler();

  useEffect(() => {
    if (userProfile?.marked_for_removal) {
      setIsMarkedForRemoval(true);
    }
  }, [userProfile]);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  return (
    <div className={styles.section}>
      <div className={styles.leadingContent}>
        <h2>Account</h2>
      </div>
      <div className={styles.trailingContent}>
        <CTAButton title="Sign out" variant="LINK" onClick={signOut} />
        {isMarkedForRemoval ? (
          <span className={styles.warningText}>
            You have marked your account for removal.
            <br />
            It will be deleted shortly.
          </span>
        ) : (
          <CTAButton
            title="Delete my account"
            destructive
            variant="LINK"
            onClick={() => {
              setShowDeleteAccountModal(true);
            }}
          />
        )}
      </div>
      <AlertModal
        title="Delete account"
        message="Are you sure you want to delete your account? All your orders and other data will be permanently deleted. This cannot be undone."
        isShowing={showDeleteAccountModal}
        dismissCTA="Cancel"
        proceedCTA="Delete"
        onDismiss={() => {
          setShowDeleteAccountModal(false);
        }}
        onProceed={() => {
          requestAccountDeletion().then(() => {});
        }}
      />
    </div>
  );
};
